import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    
    // Default to dark theme
    return 'dark';
  });

  // Function to update all theme-related meta tags and styles
  const updateThemeColors = (newTheme) => {
    const lightColor = '#ffffff';
    const darkColor = '#0f1419';
    const currentColor = newTheme === 'dark' ? darkColor : lightColor;
    
    // Update HTML and body background immediately
    document.documentElement.style.backgroundColor = currentColor;
    document.body.style.backgroundColor = currentColor;
    
    // Remove ALL existing theme-color meta tags
    document.querySelectorAll('meta[name="theme-color"]').forEach(el => el.remove());
    
    // Create new theme-color meta tag
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = currentColor;
    document.head.insertBefore(meta, document.head.firstChild);
    
    // Update or create msapplication-navbutton-color
    let msAppMeta = document.querySelector('meta[name="msapplication-navbutton-color"]');
    if (!msAppMeta) {
      msAppMeta = document.createElement('meta');
      msAppMeta.name = 'msapplication-navbutton-color';
      document.head.appendChild(msAppMeta);
    }
    msAppMeta.content = currentColor;
    
    // Update apple-mobile-web-app-status-bar-style for iOS
    let statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!statusBarMeta) {
      statusBarMeta = document.createElement('meta');
      statusBarMeta.name = 'apple-mobile-web-app-status-bar-style';
      document.head.appendChild(statusBarMeta);
    }
    statusBarMeta.content = newTheme === 'dark' ? 'black-translucent' : 'default';
    
    // Force iOS Safari to update by toggling a tiny DOM change
    const tempStyle = document.createElement('style');
    tempStyle.textContent = ':root{}';
    document.head.appendChild(tempStyle);
    setTimeout(() => tempStyle.remove(), 10);
  };

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
    
    // Update theme colors immediately
    updateThemeColors(theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    // Pre-update colors for instant feedback
    updateThemeColors(newTheme);
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
